import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';

/**
 * FirstPromoter referral tracking integration (fpr.js).
 *
 * The landing site is where promoter links land (uptal.com?fpr=... /
 * ?via=...), so the tracking script runs sitewide here:
 * - Install the official `fpr()` command-queue stub, queue `init` + `click`,
 *   and inject fpr.js (browser-only, env-gated)
 * - Read back the `_fprom_tid` visitor cookie the script sets
 *
 * fpr.js sets its cookies on the top domain (`.uptal.com`), so a click
 * tracked here is also readable from app.uptal.com. The tid is forwarded to
 * the backend on /auto-apply/subscribe (and stamped into the Moyasar payment
 * metadata) so a referred purchase is attributed to the promoter.
 */
/** localStorage key for the first-party affiliate ref capture. */
const AFFILIATE_REF_KEY = 'aa_affiliate_ref';
/** Attribution window for a captured ref (matches FP's default cookie life). */
const AFFILIATE_REF_TTL_MS = 90 * 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class FirstPromoterService {
  private scriptInjected = false;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // First-party ref capture runs UNCONDITIONALLY — our own attribution must
    // not depend on the FP script being enabled or surviving ad-blockers.
    this.captureReferralRef();

    const config = (environment as any).referral?.firstPromoter;
    if (!config?.enabled) return;
    if (!config.accountId) return;
    if (this.scriptInjected) return;

    this.scriptInjected = true;

    // Official FirstPromoter stub: commands queue until fpr.js loads.
    const w = window as any;
    w.fpr =
      w.fpr ||
      function () {
        w.fpr.q = w.fpr.q || [];
        arguments[0] === 'set'
          ? w.fpr.q.unshift(arguments)
          : w.fpr.q.push(arguments);
      };
    w.fpr('init', { cid: config.accountId });
    w.fpr('click');

    const script = document.createElement('script');
    script.async = true;
    script.src = config.scriptUrl;
    document.head.appendChild(script);
  }

  /** Visitor tracking id from the `_fprom_tid` cookie, or null. */
  getVisitorTrackingId(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    const match = document.cookie.match(/(?:^|;\s*)_fprom_tid=([^;]+)/);
    if (!match) return null;

    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }

  /**
   * The affiliate ref code the visitor arrived with: our own capture first
   * (survives ad-blockers), FP's `_fprom_ref` cookie as fallback.
   */
  getAffiliateRef(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    try {
      const raw = localStorage.getItem(AFFILIATE_REF_KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        if (
          stored?.ref &&
          Date.now() - (stored.at || 0) < AFFILIATE_REF_TTL_MS
        ) {
          return String(stored.ref).slice(0, 100);
        }
      }
    } catch {
      /* storage disabled — fall through to the cookie */
    }

    const match = document.cookie.match(/(?:^|;\s*)_fprom_ref=([^;]+)/);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]).slice(0, 100);
    } catch {
      return match[1].slice(0, 100);
    }
  }

  /**
   * Persist the promoter ref from the landing URL (?fpr=/?via=/?ref=), fresh
   * click wins — the same params fpr.js recognises.
   */
  private captureReferralRef(): void {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('fpr') || params.get('via') || params.get('ref');
      if (!ref) return;

      localStorage.setItem(
        AFFILIATE_REF_KEY,
        JSON.stringify({ ref: ref.slice(0, 100), at: Date.now() }),
      );
    } catch {
      /* storage disabled — FP's own cookie remains the only capture */
    }
  }
}
